import staffFetch from "./staffFetch";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

const API = "https://live-evo.com/api/staff/lives/alerts";
const LIVES_API = "https://live-evo.com/api/staff/lives";

function token() {
  return localStorage.getItem("evoAccessToken") || "";
}

export default function StaffStreamAlertPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await staffFetch(API, {
        headers: {Authorization: `Bearer ${token()}`}
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Chargement impossible.");
      }

      setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
      setError("");
    } catch (err) {
      setError(err.message || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  async function resolveAlert(alert) {
    if (!window.confirm(
      `Clore l’alerte #${alert.id} ? Cela ne prévient pas le créateur et ne coupe pas le live.`
    )) {
      return;
    }

    setProcessingId(alert.id);
    setError("");

    try {
      const response = await staffFetch(
        `${API}/${alert.id}/resolve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`
          },
          body: JSON.stringify({})
        }
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message ||
          "Impossible de traiter l’alerte.");
      }

      await loadAlerts();
    } catch (err) {
      setError(err.message || "Décision impossible.");
    } finally {
      setProcessingId(null);
    }
  }

  async function moderateLive(alert, action) {
    if (!alert.live_id || alert.live_status !== "active") return;

    const label = action === "stop" ? "couper" : "avertir";
    const reason = window.prompt(
      `Motif pour ${label} le live #${alert.live_id} de @${alert.host_pseudo || "créateur inconnu"} :`
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError("Indique un motif avant d’intervenir.");
      return;
    }
    if (action === "stop" && !window.confirm(
      `Confirmer la coupure du live #${alert.live_id} ?`
    )) return;

    setProcessingId(alert.id);
    setError("");
    try {
      const response = await staffFetch(
        `${LIVES_API}/${alert.live_id}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`
          },
          body: JSON.stringify({reason: reason.trim()})
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || "Action impossible.");
      }
      window.alert(
        action === "stop"
          ? "Coupure enregistrée. Vérifie le live avant de clore l’alerte."
          : "Avertissement enregistré. Vérifie sa réception avant de clore l’alerte."
      );
      await loadAlerts();
    } catch (err) {
      setError(err.message || "Action impossible.");
    } finally {
      setProcessingId(null);
    }
  }

  return (
    <main style={styles.page}>
      <div style={styles.content}>
        <button onClick={() => navigate("/staff")} style={styles.back}>
          ‹ Centre Staff
        </button>
        <p style={styles.eyebrow}>MODÉRATION LIVE</p>
        <h1 style={styles.title}>Stream Alert</h1>
        <p style={styles.subtitle}>
          {alerts.length} alerte(s) ouverte(s) à examiner et traiter.
        </p>

        {error && <p role="alert" style={styles.error}>{error}</p>}
        {loading && <p style={styles.muted}>Chargement…</p>}
        {!loading && !error && alerts.length === 0 && (
          <div style={styles.empty}>Aucune alerte ouverte.</div>
        )}

        {alerts.map(alert => (
          <article key={alert.id} style={styles.card}>
            <div style={styles.row}>
              <span style={styles.badge}>
                {alert.severity || "alerte"}
              </span>
              <span style={styles.muted}>
                #{alert.id} · {alert.created_at
                  ? new Date(alert.created_at).toLocaleString("fr-FR")
                  : "Date inconnue"}
              </span>
            </div>

            <h2 style={styles.cardTitle}>{alert.message}</h2>
            <p style={styles.muted}>
              Créateur du live : <strong>@{alert.host_pseudo || "Inconnu"}</strong>
            </p>
            {alert.alert_type === "reports" && (
              <p style={styles.muted}>
                Signalé par : <strong>@{alert.reporter_pseudo || "Inconnu"}</strong>
              </p>
            )}
            <p style={styles.muted}>
              Live #{alert.live_id || "inconnu"} :{" "}
              <strong>{alert.title || "Titre inconnu"}</strong> ·{" "}
              {alert.live_status === "active"
                ? "En cours"
                : "Terminé ou indisponible"}
            </p>

            {alert.live_id && alert.live_status === "active" && (
              <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
                <button type="button" style={styles.action}
                  disabled={processingId === alert.id}
                  onClick={() => moderateLive(alert, "warn")}>
                  Avertir le créateur
                </button>
                <button type="button" style={styles.action}
                  disabled={processingId === alert.id}
                  onClick={() => moderateLive(alert, "stop")}>
                  Couper le live
                </button>
              </div>
            )}
            <button
              type="button"
              disabled={processingId === alert.id}
              onClick={() => resolveAlert(alert)}
              style={styles.action}>
              {processingId === alert.id
                ? "Action en cours…"
                : "Clore l’alerte"}
            </button>
          </article>
        ))}
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top, #071a2b, #020912 42%, #010409)",
    color: "#fff",
    padding: "28px 16px 60px"
  },
  content: {maxWidth: "900px", margin: "0 auto"},
  back: {
    color: "#00eaff",
    background: "#091824",
    border: "1px solid #20546b",
    borderRadius: "12px",
    padding: "11px 15px",
    cursor: "pointer"
  },
  eyebrow: {
    color: "#00eaff",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "2px",
    marginTop: "31px"
  },
  title: {fontSize: "32px", margin: "5px 0"},
  subtitle: {color: "#92adbe", marginBottom: "23px"},
  error: {
    background: "#3c1323",
    border: "1px solid #d9496c",
    borderRadius: "12px",
    padding: "14px"
  },
  empty: {
    background: "#091824",
    border: "1px solid #1e475d",
    borderRadius: "16px",
    padding: "28px",
    color: "#a6c4d2"
  },
  card: {
    padding: "20px",
    marginBottom: "13px",
    border: "1px solid #20506b",
    borderRadius: "17px",
    background: "linear-gradient(140deg, #0a2031, #06111f)"
  },
  row: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "center"
  },
  badge: {
    color: "#001921",
    background: "#00eaff",
    borderRadius: "10px",
    padding: "4px 9px",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase"
  },
  muted: {color: "#a1b9c9", fontSize: "13px"},
  cardTitle: {fontSize: "17px", lineHeight: 1.45},
  action: {
    marginTop: "9px",
    padding: "11px 15px",
    background: "#074356",
    color: "#fff",
    border: "1px solid #00eaff",
    borderRadius: "11px",
    fontWeight: 800,
    cursor: "pointer"
  }
};
